import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { LayerApi } from '../../../api/layer-api';
import { BooleanConverter, InputConverter } from '../../../inputConverter';
import { GisToolsService } from '../../../services/gis-tools.service';
import { LayerService } from '../../../services/layer.service';
import { SpatialQueryService } from '../../../services/spatialQuery.service';
import { ToSqlStringService } from '../../../services/to-sql-string.service';
import { ICounty, IMapcoreMapImageLayer, IQuery } from '../../../types';
import { BaseComponent } from '../../base.component';

/** Geographic Filter Component */
@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-geographic-filter-ng',
    styleUrls: ['../../sharedStyles/base.scss', './geographic-filter.component.scss', '../../sharedStyles/telerik.scss'],
    templateUrl: './geographic-filter.component.html'
})
export class GeographicFilterComponent extends BaseComponent implements OnDestroy {
    @Input() containerElement: any;
    @Input() boundaryLayerIds: number[];
    @Input() boundarySource: 'system' | 'draw' = 'system';
    @Input() workingGuid: string;
    @Input() esriLayers: NonNullable<IMapcoreMapImageLayer[]>;
    @Input() esriLayerId: string;
    @Input() layerId: number;
    @Input() mapView: NonNullable<__esri.MapView>;
    @Input() selectedLayerId: number = 0;
    @Input() selectedCountyCode: string;
    @Input() selectedFeatures: any[] = [];
    @Input() query: IQuery;
    @Input() @InputConverter(BooleanConverter) runSpatialQueryOnSketchComplete: boolean = true;
    @Input() @InputConverter(BooleanConverter) reactivateSketchOnSketchComplete: boolean = true;
    @Input() spatialQuerySelection = false;
    @Input() disableField = false;
    @Input() resetComponent = () => {
        this.reset();
    }
    @Input() boundaryTypes: BoundaryType[];
    @Input()
    @InputConverter()
    updateLayerOnApply: boolean = true;

    @Output() public readonly onfeatureschanged = new EventEmitter<{ layerId: number; features: any[]; boundaryType: BoundaryType; geometryString: string }>();
    @Output() public readonly onboundarysourcechanged = new EventEmitter<string>();
    @Output() public readonly selectedGeometryChange = new EventEmitter<{ geometryString: string}>();
    @Output() public readonly onapply = new EventEmitter<{ id?: number; boundaryFeatures?: any[]; geometryStrings?: string[]; esriLayerId?: string}>();
    @Output() public readonly onapplyLayer = new EventEmitter<{ id?: string; esriLayer?: any; data?: any }>();
    @Output() public readonly ondone = new EventEmitter<{ query?: IQuery; geometryString?: string; esriLayerId?: string }>();
    @Output() public readonly ondrawcomplete = new EventEmitter<__esri.Geometry[]>();
    @Output() public readonly ondraw = new EventEmitter<boolean>();

    @Input() counties: ICounty[];

    @ViewChild('lassoTool') lassoTool: any;
    _isActivated: boolean = false;
    @Input()
    get isActivated(): boolean {
        return this._isActivated;
    }

    constructor(_vcr: ViewContainerRef, private readonly changeDetectorRef: ChangeDetectorRef, private readonly gisToolsService: GisToolsService,
        private readonly layerApi: LayerApi, private readonly layerService: LayerService, private readonly spatialQueryService: SpatialQueryService,
        public toSqlStringService: ToSqlStringService) {
        super(_vcr);
        this.registerEvent('onapply', this.onapply);
        this.registerEvent('onapplyLayer', this.onapplyLayer);
        this.registerEvent('ondone', this.ondone);
        this.registerEvent('ondrawcomplete', this.ondrawcomplete);
        this.registerEvent('ondraw', this.ondraw);
        this.registerEvent('onfeatureschanged', this.onfeatureschanged);
        this.registerEvent('onboundarysourcechanged', this.onboundarysourcechanged);
        this.registerEvent('onselectedgeometrychange', this.selectedGeometryChange);
    }

    @Input()
    public activate = async () => {
        if (this.lassoTool) {
            this.lassoTool.containerElement = this.containerElement || this.hostElement;
            await this.reset();
            if (!this._isActivated) {
                this.lassoTool.oncomplete.subscribe(() => {
                    this.lassoComplete();
                });
                this._isActivated = true;
                this.lassoTool.drawEnd.subscribe((data) => {
                    this.ondrawcomplete.emit(data);
                    this.onboundarysourcechanged.emit(null);
                });
            }
            this.changeDetectorRef.detectChanges();
        }
    }

    @Input()
    public applySpatialFilterToEsriLayers = async (esriLayers: IMapcoreMapImageLayer[], geometries: string[]) => {
        const dynamicLayerSpatialLayers = esriLayers.map(x => this.spatialQueryService.getIDynamicLayerSpatialFromLayer(x));
        const results: [{ id: string; guid: string }] = await this.spatialQueryService.spatialQuery(true, dynamicLayerSpatialLayers, null, null, null, geometries);
        for (const result of results) {
            this.workingGuid = result.guid;
        }
        this.applyFilterChangeToLayer();
    }

    get showCountySelect() {
        return this.selectedLayerId && this.boundaryTypes &&
            this.selectedLayerId === this.getBoundaryTypeByKey('municipality').layerId;
    }

    get showFeatureSelect() {
        return this.selectedLayerId && (!this.showCountySelect || this.selectedCountyCode);
    }

    get features() {
        if (this.selectedLayerId > 0) {
            return this.getSelectedBoundaryType().features;
        }
        return [];
    }

    get existingGuidFromLayers(): string {
        return this.getExistingGuidFromLayers();
    }

    get boundaryFeaturesAreSelected() {
        return this.boundarySource === 'system' && !this.existingGuidFromLayers && this.selectedFeatures && this.selectedFeatures.length;
    }

    getExistingGuidFromLayers(): string {
        if (this.esriLayers && this.esriLayers.length && this.esriLayers.length > 0) {
            const results = this.esriLayers.filter(x => x && x.mapcoreProps && x.mapcoreProps.query && this.getGuidIfExistsAnywhere(x.mapcoreProps.query));
            if (results.length) {
                const firstLayer = results[0];
                if (firstLayer.mapcoreProps && firstLayer.mapcoreProps.query) {
                    return this.getGuidIfExistsAnywhere(firstLayer.mapcoreProps.query);
                }
            }
        }
        return null;
    }

    getGuidIfExistsAnywhere(query: IQuery) {
        if (query) {
            if (this.getGuidIfExistsInRoot(query)) {
                return this.getGuidIfExistsInRoot(query);
            } else if (query.subqueries) {
                const foundGuidQueries = query.subqueries.filter(x => this.getGuidIfExistsInRoot(x));
                if (foundGuidQueries.length && foundGuidQueries.length > 0) {
                    return foundGuidQueries[0].value;
                }
            }
        }
        return null;
    }

    getGuidIfExistsInRoot(query: IQuery) {
        if (query.operator && query.operator.indexOf('GUID') >= 0) {
            return query.value;
        }
    }

    ngOnDestroy() {
        this.boundarySource = 'system';
        this.reset();
    }

    lassoComplete() {
        this.workingGuid = this.lassoTool.guid;
        this.changeDetectorRef.detectChanges();
    }

    async reset() {
        //this.boundarySource = 'system';
        this.lassoTool.deactivate();
        this.workingGuid = this.existingGuidFromLayers;
        this.selectedLayerId = 0;
        this.selectedCountyCode = null;
        this.counties = null;
        this.boundaryTypes = [];
        this.selectedFeatures = [];
        this.populateBoundaryTypes();
        await this.populateBoundaryFeatures();
        this.populateCountyArray();
        this.applyBoundaryTypeFeatureDisplayText();
        this.selectedGeometryChange.emit({geometryString: null});
    }

    populateBoundaryTypes() {
        this.boundaryTypes = [];
        this.addBoundaryType('county', 'counties', 'County Boundaries', (x) => `${x.countyCode} - ${x.name}`);
        this.addBoundaryType('district', 'districts', 'District Boundaries', (x) => `District - ${x.districtNumber}`);
        this.addBoundaryType('municipality', 'municipalities', 'Municipality Boundaries', (x) => `${x.name} - ${x.municipalityCode}`);
        this.addBoundaryType('senateDistrict', 'pasenatedistricts', 'PA Senate Districts', (x) => `District ${x.legislativeDistrictNumber} - ${x.lastName} ${x.firstName}`);
        this.addBoundaryType('houseDistrict', 'pahousedistricts', 'PA House Districts', (x) => `District ${x.legislativeDistrictNumber} - ${x.lastName} ${x.firstName}`);
        this.addBoundaryType('congressDistrict', 'uscongressionaldistricts', 'US Congress Districts', (x) => `District ${x.legislativeDistrictNumber} - ${x.lastName} ${x.firstName}`);
        this.addBoundaryType('assistantAreasWinter', 'assistantAreasWinter', 'Assistant Areas Winter', (x) => `Org Code ${x.orgCode} - SAP Plant ${x.sapPlant} - ${x.firstName} ${x.lastName}`);
        this.addBoundaryType('assistantAreasSummer', 'assistantAreasSummer', 'Assistant Areas Summer', (x) => `Org Code ${x.orgCode} - SAP Plant ${x.sapPlant} - ${x.firstName} ${x.lastName}`);
    }

    async populateBoundaryFeatures() {
        [
            this.getBoundaryTypeByKey('county').features,
            this.getBoundaryTypeByKey('district').features,
            this.getBoundaryTypeByKey('municipality').features,
            this.getBoundaryTypeByKey('senateDistrict').features,
            this.getBoundaryTypeByKey('houseDistrict').features,
            this.getBoundaryTypeByKey('congressDistrict').features,
            this.getBoundaryTypeByKey('assistantAreasWinter').features,
            this.getBoundaryTypeByKey('assistantAreasSummer').features
        ] = await Promise.all([
            this.gisToolsService.getAllCounties('objectId, countyCode, name'),
            this.gisToolsService.getAllDistricts('objectId, districtNumber'),
            this.gisToolsService.getAllMunicipalities('objectId, countyCode, municipalityCode, municipalityClass, name'),
            this.gisToolsService.getAllSenateDistricts('objectId, firstName, lastName, legislativeDistrictNumber'),
            this.gisToolsService.getAllHouseDistricts('objectId, firstName, lastName, legislativeDistrictNumber'),
            this.gisToolsService.getAllCongressionalDistricts('objectId, firstName, lastName, legislativeDistrictNumber'),
            this.getAssistAreasForWinter(),
            this.getAssistAreasForSummer()
        ]);
        this.sortFeaturesByAProperty('county', 'countyCode');
        this.sortFeaturesByAProperty('district', 'districtNumber');
        this.sortFeaturesByAProperty('municipality', 'name');
        this.sortFeaturesByAProperty('senateDistrict', 'lastName');
        this.sortFeaturesByAProperty('houseDistrict', 'lastName');
        this.sortFeaturesByAProperty('congressDistrict', 'lastName');
        this.sortFeaturesByAProperty('assistantAreasWinter', 'orgCode');
        this.sortFeaturesByAProperty('assistantAreasSummer', 'orgCode');
    }

    setUseSystemBoundary() {
        this.boundarySource = 'system';
        this.onboundarysourcechanged.emit(this.boundarySource);
        this.lassoTool.clearFeatureResults();
        this.lassoTool.deactivate();
    }

    setDrawBoundaryOnMap() {
        this.lassoTool.deactivate();
        this.boundarySource = 'draw';
        this.onboundarysourcechanged.emit(this.boundarySource);
        this.lassoTool.mapView = this.mapView;
        this.lassoTool.esriLayers = this.esriLayers;
        this.lassoTool.spatialQueryType = 'geoprocessing';
        this.lassoTool.runSpatialQueryOnSketchComplete = this.runSpatialQueryOnSketchComplete;
        this.lassoTool.reactivateSketchOnSketchComplete = false;
        this.lassoTool.clearFeatureResults();
        this.lassoTool.activate();
        this.ondraw.emit(true);
    }

    populateCountyArray() {
        this.counties = [];
        for (const feature of this.getBoundaryTypeByKey('county').features) {
            const county = (<ICounty><any>feature);
            this.counties.push({ countyCode: county.countyCode, name: county.name });
        }
    }

    applyBoundaryTypeFeatureDisplayText() {
        for (const boundaryType of this.boundaryTypes) {
            this.applyDisplayTextToThisBoundaryType(boundaryType);
        }
    }

    applyDisplayTextToThisBoundaryType(boundaryType: BoundaryType) {
        if (boundaryType.getDisplayTextFunc) {
            for (const feature of boundaryType.features) {
                feature.displayText = boundaryType.getDisplayTextFunc(feature);
            }
        }
    }

    addBoundaryType(key: string, apiQueryName: string, displayText: string, getDisplayTextFunc: (x: any) => string = null) {
        const boundaryType: BoundaryType = { key, displayText, apiQueryName, getDisplayTextFunc, features: [] };
        boundaryType.layerId = this.spatialQueryService.getLayerIdByName(key);
        this.boundaryTypes.push(boundaryType);
    }

    layerIdChanged() {
        this.selectedFeatures = [];
        this.emitSelectedFeatures();
        this.changeDetectorRef.detectChanges();
    }

    async applySystemBoundaryFilter() {
        const selectedBoundaryType = this.getSelectedBoundaryType();
        const geometryString = await this.spatialQueryService.getGeometry(this.selectedFeatures, selectedBoundaryType);
        if (this.esriLayers && this.esriLayers.length && this.esriLayers.length > 0) {
            this.ondone.emit({ geometryString });
            await this.applySpatialFilterToEsriLayers(this.esriLayers, [geometryString]);
        } else {
            Promise.resolve(this.selectedGeometryChange.emit({ geometryString }));
            this.ondone.emit({ geometryString });

        }
        this.onapply.emit({ id: this.layerId, geometryStrings: [geometryString], esriLayerId: this.esriLayerId});
    }

    applyFilterChangeToLayer() {
        this.updateLayerWithGeographicFilter();
        this.lassoTool.clearFeatureResults();
        this.lassoTool.deactivate();
        const data: any = { query: this.query };
        this.ondone.emit(data);

        if (this.updateLayerOnApply) {
            this.onapplyLayer.emit({ id: this.layerId.toString(), data });
        }
    }

    removeGeographicFilter() {
        this.updateLayerWithGeographicFilter();
        this.clearExistingSpatialFilter();
    }

    updateLayerWithGeographicFilter() {
        for (const esriLayer of this.esriLayers) {
            if (this.workingGuid) {
                const queryWithGuid = this.spatialQueryService.getGuidQuery(this.workingGuid);
                this.query = this.toSqlStringService.addFilter(esriLayer.mapcoreProps.query, queryWithGuid);
            } else {
                this.query = this.getQueryWithGuidRemoved(esriLayer);
            }
            this.layerService.updateLayerFilter(esriLayer, this.query);
        }
    }

    clearExistingSpatialFilter() {
        this.workingGuid = null;
        this.updateLayerWithGeographicFilter();
        if (this.boundarySource === 'draw') {
            this.lassoTool.clearFeatureResults();
            this.lassoTool.activate();
        }
        this.changeDetectorRef.detectChanges();
    }

    getQueryWithGuidRemoved(esriLayer: IMapcoreMapImageLayer) {
        const mapcoreProps = esriLayer.mapcoreProps;
        if (mapcoreProps && mapcoreProps.query) {
            const layerQuery = this.toSqlStringService.getSimplifiedDeepCopyOfQuery(mapcoreProps.query);
            if (this.getGuidIfExistsInRoot(layerQuery)) {
                return {};
            } else if (layerQuery.subqueries) {
                layerQuery.subqueries = layerQuery.subqueries.filter(x => !this.getGuidIfExistsInRoot(x));
            }
            return layerQuery;
        }
    }

    async selectedCountyChange() {
        const municipalityBoundaryType = this.getBoundaryTypeByKey('municipality');
        this.selectedFeatures = [];
        municipalityBoundaryType.features = [];
        this.emitSelectedFeatures();
        this.changeDetectorRef.detectChanges();

        municipalityBoundaryType.features =
            await this.gisToolsService.getMunicipalitiesByCounty(this.selectedCountyCode, 'objectId, countyCode, municipalityCode, municipalityClass, name');
        this.applyDisplayTextToThisBoundaryType(municipalityBoundaryType);
        this.emitSelectedFeatures();
    }

    cancel() {
        this.lassoTool.clearFeatureResults();
        this.reset();
        if (this.esriLayers && this.esriLayers.length && this.esriLayers.length > 0) {
            this.ondone.emit(null);
        } else {
            this.selectedGeometryChange.emit({geometryString: null});
            this.onapply.emit({ id: this.layerId, geometryStrings: [null], esriLayerId: this.esriLayerId});
        }
    }

    selectedFeaturesChanged() {
        this.emitSelectedFeatures();
    }

    async emitSelectedFeatures() {
        const selectedBoundaryType = this.getSelectedBoundaryType();
        const geometryString: string = await this.spatialQueryService.getGeometry(this.selectedFeatures, selectedBoundaryType);
        this.onfeatureschanged.emit({ layerId: this.selectedLayerId, features: this.selectedFeatures, boundaryType: this.getSelectedBoundaryType(), geometryString });
        this.selectedGeometryChange.emit({ geometryString });
        this.changeDetectorRef.detectChanges();
    }

    getBoundaryTypeByKey(key: string) {
        return this.boundaryTypes.filter(x => x.key === key)[0];
    }

    getSelectedBoundaryType(): BoundaryType {
        return this.getBoundaryTypeByLayerId(this.selectedLayerId);
    }

    getBoundaryTypeByLayerId(layerId: number) {
        return this.boundaryTypes.filter(x => x.layerId === layerId)[0];
    }

    sortFeaturesByAProperty(key: string, prop: string) {
        const features = this.getBoundaryTypeByKey(key).features;
        this.getBoundaryTypeByKey(key).features = this.gisToolsService.orderBy(features, prop);
    }

    async getAssistAreasForWinter(): Promise<AssistantArea[]> {
        const result = await this.layerApi.getData(this.spatialQueryService.getLayerIdByName('assistantAreasWinter'),
            ['objectid', 'org_code', 'sap_plant', 'first_name_shift_a', 'last_name_shift_a'], { field: 'sap_plant', operator: 'IS NOT NULL' });
        return result.map(x => this.getAssistantAreaFromDataResult(x));
    }

    async getAssistAreasForSummer(): Promise<AssistantArea[]> {
        const result = await this.layerApi.getData(this.spatialQueryService.getLayerIdByName('assistantAreasSummer'),
            ['objectid', 'org_code', 'sap_plant', 'first_name', 'last_name'], { field: 'sap_plant', operator: 'IS NOT NULL' });
        return result.map(x => this.getAssistantAreaFromDataResult(x));
    }

    getAssistantAreaFromDataResult(row: string[]): AssistantArea {
        const [objectId, orgCode, sapPlant, firstName, lastName, geometry] = row;
        return {
            objectId: parseInt(objectId, 10),
            orgCode,
            sapPlant,
            firstName,
            lastName,
            geometry
        } as AssistantArea;
    }
    get showApplyButton() {
        return this.boundarySource === 'draw' && !this.existingGuidFromLayers && this.workingGuid && !this.spatialQuerySelection;
    }
}

export class BoundaryType {
    key?: string;
    apiQueryName?: string;
    displayText?: string;
    layerId?: number;
    features?: BoundaryFeature[];
    getDisplayTextFunc?: (x: any) => string;
}

export class BoundaryFeature {
    objectId: number;
    displayText?: string;
    geometry: string;
}

export class AssistantArea extends BoundaryFeature {
    orgCode?: string;
    sapPlant?: string;
    firstName?: string;
    lastName?: string;
}
